import { useEffect, useRef } from 'react';
import useQueryString from './use-query-string';

// Stamps link attribution from the page URL onto the cart, once per
// (cart, message). See useLinkAttribution below for where this is used; the
// logic lives in this plain function so it can be unit-tested without React.
//
// Semantics are LAST-touch, matching the Sittings page: a newer campaign's
// message_id replaces an older one on a long-lived cart, so December's
// holiday campaign isn't credited to June's picture-day send. The stampedSet
// keeps CustomerContext's cart-instance churn (re-fetches swap in new
// objects for the same row) from double-PATCHing the same attribution. On a
// failed save the local mutation is reverted too -- otherwise the equality
// guard would see the unsaved value and permanently block the retry.
// entry_method is NOT set here: the server derives it from the arriving
// message_id.
export function stampLinkAttribution(cart, { messageId, from }, stampedSet) {
  if (!cart || !cart.save || !cart.id || !messageId) return false;

  const key = `${cart.id}:${messageId}`;
  if (stampedSet.has(key)) return false;
  if (String(cart.messageId) === String(messageId)) return false;

  const previous = { messageId: cart.messageId, createdLocation: cart.createdLocation };
  stampedSet.add(key);
  cart.messageId = messageId;
  if (from) cart.createdLocation = from;
  cart.save().catch(() => {
    cart.messageId = previous.messageId;
    cart.createdLocation = previous.createdLocation;
    stampedSet.delete(key);
  });
  return true;
}

// Campaign and parent-ready links carry `message_id` (and `from=`) so a
// purchase can be credited to the exact send that produced it. The Sittings
// page consumes them inline; pages that campaign links deep-link to
// (PreviousImages v2 -> quick order, senior quick order, gifts builder)
// historically DROPPED them, so those orders showed as unattributed. Use
// this on any page a sent link can land on.
export default function useLinkAttribution(cart) {
  const { message_id: messageId, from } = useQueryString();
  const stamped = useRef(new Set());
  const cartId = cart && cart.id;

  useEffect(() => {
    stampLinkAttribution(cart, { messageId, from }, stamped.current);
  }, [cart, cartId, messageId, from]);
}
